import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Camera, Crop as CropIcon } from 'lucide-react';
import { OroLoader } from './OroLoader';
import Button from './Button';
import ImageCropModal from './ImageCropModal';

interface HeroImageUploadProps {
  currentImageUrl?: string | null;
  onImageChange: (imageUrl: string | null) => void;
  onImageUpload: (file: File) => Promise<string>;
  onImageRemove?: () => Promise<void>;
  disabled?: boolean;
  className?: string;
  enableCrop?: boolean;
  /** Compact mode for mobile - minimal UI, camera icon, no tips section */
  compact?: boolean;
}

export function HeroImageUpload({
  currentImageUrl,
  onImageChange,
  onImageUpload,
  onImageRemove,
  disabled = false,
  className = '',
  enableCrop = true,
  compact = false
}: HeroImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false); // #1107: Track image load for fade-in
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset image loaded state when URL changes (#1107)
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  // File validation constants - optimized for hero images
  // CF Image Resizing handles optimization (#792), so we accept larger uploads
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB (CF Image Resizing handles optimization)
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const RECOMMENDED_DIMENSIONS = { width: 1200, height: 675 };

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload JPG, PNG, or WebP images only.';
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 20MB.';
    }
    
    return null;
  };

  const handleFile = async (file: File) => {
    setError(null);
    
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (enableCrop) {
      // Show crop modal for user to crop the image
      setPendingFile(file);
      setShowCropModal(true);
    } else {
      // Upload directly without cropping
      await uploadFile(file);
    }
  };

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    
    try {
      const imageUrl = await onImageUpload(file);
      onImageChange(imageUrl);
    } catch (error: unknown) {
      console.error('Hero image upload failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropComplete = async (croppedFile: File) => {
    setShowCropModal(false);
    setPendingFile(null);
    await uploadFile(croppedFile);
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setPendingFile(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    // Reset input value to allow re-uploading the same file
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || isUploading) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      handleFile(imageFile);
    } else {
      setError('Please drop an image file');
    }
  };

  const handleRemoveImage = async () => {
    if (onImageRemove) {
      setIsUploading(true);
      setError(null);
      try {
        await onImageRemove();
        onImageChange(null);
      } catch (error: unknown) {
        console.error('Hero image removal failed:', error);
        setError(error instanceof Error ? error.message : 'Failed to remove image. Please try again.');
      } finally {
        setIsUploading(false);
      }
    } else {
      onImageChange(null);
      setError(null);
    }
  };

  const triggerFileInput = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Current Image Preview */}
      {currentImageUrl && (
        <div className="relative group">
          <div className="aspect-video rounded-xl overflow-hidden bg-surface-alt border border-border">
            {/* Loading placeholder - shows subtle animation until image loads (#1107) */}
            {!imageLoaded && (
              <div className="absolute inset-0 bg-oro-surface-alt animate-pulse" />
            )}
            <img
              src={currentImageUrl}
              alt="Club hero image"
              className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              decoding="async"
              onLoad={handleImageLoad}
            />
            {/* Remove button */}
            <button
              onClick={handleRemoveImage}
              disabled={isUploading}
              className="absolute top-2 right-2 p-2 md:p-1.5 bg-oro-error text-white rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-oro-error/80 disabled:opacity-50 touch-manipulation"
              aria-label="Remove hero image"
            >
              <X className="w-4 h-4" />
            </button>
            {/* Change photo button overlay - compact mode */}
            {compact && (
              <button
                onClick={triggerFileInput}
                disabled={disabled || isUploading}
                className="absolute top-2 left-2 p-2 bg-black/50 hover:bg-black/70 rounded-lg transition-all disabled:opacity-50"
                aria-label="Change cover photo"
              >
                <Camera className="w-5 h-5 text-white" />
              </button>
            )}
          </div>
          {!compact && (
            <p className="text-xs text-text-muted mt-2">
              Current hero image • Click to replace or remove
            </p>
          )}
        </div>
      )}

      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={triggerFileInput}
        className={`
          aspect-video border-2 border-dashed rounded-xl transition-all cursor-pointer
          flex flex-col items-center justify-center text-center
          ${compact ? 'p-4' : 'p-8'}
          ${dragActive
            ? 'border-accent bg-accent/10'
            : 'border-border hover:border-border-hover hover:bg-surface-alt/50'
          }
          ${disabled || isUploading ? 'cursor-not-allowed opacity-50' : ''}
          ${currentImageUrl ? 'hidden' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={disabled || isUploading}
          className="hidden"
        />

        {isUploading ? (
          <>
            <OroLoader size={compact ? 40 : 56} className={compact ? 'mb-2' : 'mb-4'} decorative />
            <p className={`${compact ? 'text-sm' : 'text-lg'} font-medium text-text-primary ${compact ? '' : 'mb-2'}`}>
              {compact ? 'Uploading...' : 'Uploading...'}
            </p>
            {!compact && <p className="text-sm text-text-muted">Please wait while we process your image</p>}
          </>
        ) : compact ? (
          /* Compact mode - minimal mobile UI */
          <>
            <Camera className="w-10 h-10 text-text-muted mb-2" />
            <span className="text-sm font-medium text-text-muted">Add Cover Photo</span>
          </>
        ) : (
          /* Full mode - desktop UI with details */
          <>
            <div className="p-3 bg-accent/10 rounded-full mb-4">
              {dragActive ? (
                <Upload className="w-8 h-8 text-accent" />
              ) : (
                <Camera className="w-8 h-8 text-accent" />
              )}
            </div>

            <p className="text-lg font-medium text-text-primary mb-2">
              {dragActive
                ? 'Drop your hero image here'
                : enableCrop
                  ? 'Upload & Crop Hero Image'
                  : 'Upload Hero Image'
              }
            </p>

            <p className="text-sm text-text-muted mb-4">
              {enableCrop
                ? 'Drag and drop an image or click to browse • Perfect cropping tools included'
                : 'Drag and drop an image or click to browse'
              }
            </p>

            <div className="space-y-1 text-xs text-text-muted">
              <p>Recommended: {RECOMMENDED_DIMENSIONS.width} × {RECOMMENDED_DIMENSIONS.height}px</p>
              <p>Maximum file size: 20MB</p>
              <p>Formats: JPG, PNG, WebP</p>
            </div>
          </>
        )}
      </div>

      {/* Replace Button for Existing Image - hidden in compact mode (uses overlay instead) */}
      {!compact && currentImageUrl && !isUploading && (
        <div className="flex gap-3">
          <Button
            variant="ghost"
            onClick={triggerFileInput}
            disabled={disabled}
            className="flex-1"
          >
            {enableCrop ? (
              <>
                <CropIcon className="w-4 h-4 mr-2" />
                Replace & Crop
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Replace Image
              </>
            )}
          </Button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-oro-error/10 border border-oro-error/40 rounded-lg">
          <p className="text-sm text-oro-error">{error}</p>
        </div>
      )}

      {/* Upload Instructions - hidden in compact mode */}
      {!compact && !currentImageUrl && !isUploading && (
        <div className="p-4 bg-surface-alt rounded-lg border border-border">
          <h4 className="text-sm font-medium text-text-primary mb-2">Hero Image Tips:</h4>
          <ul className="text-xs text-text-muted space-y-1">
            <li>• Use high-quality images for best results</li>
            <li>• Landscape orientation works best (16:9 ratio)</li>
            <li>• Ensure important content isn't near the edges</li>
            <li>• Consider how text might appear over your image</li>
          </ul>
        </div>
      )}

      {/* Image Crop Modal */}
      {showCropModal && pendingFile && (
        <ImageCropModal
          isOpen={showCropModal}
          onClose={handleCropCancel}
          imageFile={pendingFile}
          aspectRatio={16 / 9} // 16:9 for hero images (1200×675)
          onCropComplete={handleCropComplete}
          maxWidth={RECOMMENDED_DIMENSIONS.width}
          maxHeight={RECOMMENDED_DIMENSIONS.height}
        />
      )}
    </div>
  );
}

export default HeroImageUpload;